import React from 'react';
import { 
  Container, Paper, Box, Typography, Button, IconButton, 
  Snackbar, Alert, CircularProgress, Divider, Tooltip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import { ThemeProvider } from '@mui/material/styles';

import theme from '../../../theme/theme';
import { DRAFT_KEYS } from '../../../utils/draftUtils';
import DraftSaveButton from '../../../components/DraftSaveButton';

import { useAnalgesiaAssessment } from './hooks/useAnalgesiaAssessment';
import PatientInfoSection from './screens/PatientInfoSection';
import PlanSection from './screens/PlanSection';
import AnalgesiaTable from './screens/AnalgesiaTable';

const AnalgesiaAssessmentChart = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    patientData, setPatientData,
    rows, setRows,
    errors, 
    existingId,
    loading,
    submitStatus, setSubmitStatus,
    handlePatientChange, handleBlur, updateRow, addRow, deleteRow,
    handleSubmit, handleShare,
    mutationLoading
  } = useAnalgesiaAssessment(uhid_no, navigate);

  const handleBack = () => {
    navigate(-1);
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = (formName, uhid) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid)}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 4, borderRadius: '16px' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: '#008080',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1
                }}
              >
                POST OPERATIVE - ANALGESIA
              </Typography>
              <Typography variant="h5" sx={{ color: '#008080', fontWeight: 500 }}>
                ASSESSMENT CHART
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: 40, height: 40 }} />
              <Typography variant="h5" sx={{ color: '#008080', fontWeight: 700 }}>OTMS</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ borderRadius: '8px' }}
            >
              Back
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ borderRadius: '8px', backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' } }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("POST OPERATIVE ANALGESIA ASSESSMENT CHART", uhid_no)}
                sx={{ borderRadius: '8px', backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' } }}
              >
                Add Video
              </Button>
              <Tooltip title="Share with Doctor">
                <IconButton 
                  onClick={handleShare}
                  sx={{ color: '#008080', border: '1px solid #008080' }}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#008080', fontWeight: 600 }}>
                Patient Details
              </Typography>
              <PatientInfoSection 
                patientData={patientData}
                setPatientData={setPatientData}
                errors={errors}
                handlePatientChange={handlePatientChange}
                handleBlur={handleBlur}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <PlanSection 
                patientData={patientData}
                setPatientData={setPatientData}
                errors={errors}
                handlePatientChange={handlePatientChange}
                handleBlur={handleBlur}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <AnalgesiaTable 
                rows={rows}
                updateRow={updateRow}
                addRow={addRow}
                deleteRow={deleteRow}
              />
            </Box>

            {/* Form Actions */}
            <Box sx={{ display: 'flex', gap: 2, mt: 6 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT}
                uhidNo={uhid_no}
                formData={{ ...patientData, assessment_rows: rows }}
                onDraftLoaded={(draftData) => {
                  const { assessment_rows, ...pd } = draftData;
                  setPatientData(prev => ({ ...prev, ...pd }));
                  if (assessment_rows) setRows(assessment_rows);
                }}
                variant="outlined"
                size="large"
                isUpdate={!!existingId}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={mutationLoading}
                startIcon={mutationLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ 
                  borderRadius: '10px', 
                  backgroundColor: '#008080', 
                  py: 1.5,
                  '&:hover': { backgroundColor: '#006666' } 
                }}
              >
                {mutationLoading ? 'Submitting...' : (existingId ? 'Update Assessment' : 'Submit Assessment')}
              </Button>
            </Box>
          </form>

          {/* Feedback SnackBar */}
          <Snackbar 
            open={submitStatus.open} 
            autoHideDuration={6000} 
            onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))} 
              severity={submitStatus.severity} 
              sx={{ width: '100%', borderRadius: '8px' }}
            >
              {submitStatus.message}
            </Alert>
          </Snackbar>

          {existingId && (
            <Alert severity="info" sx={{ mt: 3, borderRadius: '8px' }}>
              You are updating an existing assessment record for this patient.
            </Alert>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default AnalgesiaAssessmentChart;

